import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useNotifications } from '@/contexts/NotificationContext';
import { useReauth } from '@/contexts/ReauthContext';
import { useAsyncJob } from './useAsyncJob';
import type { SyncStats } from '@/components/products/FetchProgressDialog';
import i18n from '@/lib/i18n';
import { getFlowIntent } from '@/lib/flowIntent';
import { shouldStartPreScore } from '@/lib/preScoreGate';
import { scoreTypeForFeed } from '@/lib/scoreTypeForFeed';
import { resolveGoogleAccountForFeed } from '@/lib/googleAccountForFeed';
import { GOOGLE_AUTH_STATUS_QUERY_KEY } from './useGoogleAuthStatus';

// Edge Function base URL (same Supabase project as client.ts)
const FUNCTIONS_BASE = 'https://diywpnlgowjzavjcpehw.supabase.co/functions/v1';

/**
 * Der Satz zu einem gescheiterten Sync-Job.
 *
 * `product_sync_jobs.error_message` traegt entweder einen stabilen CODE aus
 * `_shared/google-auth.ts` oder einen rohen Backend-Text. Codes bekommen hier genau
 * einen lokalisierten Satz, alles andere laeuft unveraendert durch — die meisten
 * Werte sind Google-Fehlertexte, und die zu schlucken nimmt dem Kunden die Diagnose.
 *
 * GOOGLE_REAUTH_REQUIRED steht bewusst NICHT in dieser Tabelle: der Code hat mit
 * `isReauthJobError` einen eigenen Zweig, der den Reauth-Dialog oeffnet und damit
 * mehr tut als einen Satz zu zeigen.
 *
 * Tabelle plus Funktion auf Modulebene und beides exportiert, Muster
 * SYNC_MESSAGE_CODES / `localizeSyncMessage` in SyncProgressDialog.tsx: die Zuordnung
 * bekommt damit einen eigenen, ausfuehrenden Test, und der Test kann ueber ALLE
 * Eintraege laufen statt sie abzuschreiben. Noetig ist er, weil ein fehlender Eintrag
 * NICHT auffaellt — der Fallback gibt den Code einfach zurueck, und der Kunde liest
 * ihn. Genau so kam GOOGLE_GRANT_UNAVAILABLE roh in einem destruktiven Toast an.
 * Die Funktion liest nur `i18n`, keinen Hook-Zustand.
 */
export const JOB_ERROR_CODES: Record<string, string> = {
  // Token-Fehlschlag ohne Handlungsmoeglichkeit (Google-Stoerung, unsere eigenen
  // Client-Credentials). Sagt ausdruecklich NICHT "neu verbinden".
  GOOGLE_TOKEN_UNAVAILABLE: 'dashboard:toasts.tokenUnavailableDesc',
  // Mitgliedschaft Stufe 1: kein Mitglied dieser Merchant Center hat eine lebende
  // Google-Verbindung. Eigener Satz und nicht der von GOOGLE_TOKEN_UNAVAILABLE:
  // "spaeter nochmal" waere falsch, warten heilt das nie. Und ausdruecklich nicht
  // der Reauth-Zweig: der Lesende ist womoeglich Mitglied und kann gar nichts neu
  // verbinden — handeln muss der Eigentuemer.
  GOOGLE_GRANT_UNAVAILABLE: 'dashboard:toasts.grantUnavailableDesc',
};

export const describeJobError = (errorMessage: unknown): string => {
  const key = typeof errorMessage === 'string' ? JOB_ERROR_CODES[errorMessage] : undefined;
  if (key) return i18n.t(key);
  return (typeof errorMessage === 'string' && errorMessage)
    || i18n.t('dashboard:toasts.unknownError');
};

interface ProductSyncJob {
  id: string;
  status: string;
  total_fetched: number;
  total_pages: number;
  stats_new: number | null;
  stats_updated: number | null;
  stats_deleted: number | null;
  last_heartbeat_at: string | null;
  recent_titles: string[] | null;
}

/** Fehler-Antwortkoerper von `enqueue-product-sync`. Jedes Feld optional, weil
 *  die Antwort je nach Zweig ein anderes traegt (`code` beim Reauth, `error`
 *  beim Rate-Limit) und der `catch`-Zweig unten ueberhaupt keins liefert —
 *  vorher stand hier `Record<string, unknown>`, das trug jeden Zugriff, passte
 *  aber auf keinen Parameter. Zur Laufzeit kann der Koerper mehr enthalten
 *  (z. B. `reason` fuer den Reauth-Dialog); der Spread an der Aufrufstelle
 *  reicht das unveraendert weiter. */
interface SyncEnqueueErrorBody {
  code?: string;
  error?: string;
  message?: string;
  googleAccountId?: string;
  googleAccountEmail?: string;
}

export function useFetchProducts(
  feedId: string | null,
  refetchStats: () => void,
  setScoreJobInfo: (info: { jobId: string; totalProducts: number } | null) => void,
  /**
   * When true, do not auto-open the FetchProgressDialog for a detected job.
   * Used during onboarding: the wizard's DownloadProductsStep owns the download
   * UI, so the dashboard dialog must not pop up as a duplicate (it also can't
   * track completion for a job it didn't start, and would hang).
   */
  suppressAutoDialog = false,
  /**
   * NA1 Task 1b (Ruling N3): laeuft gerade eine Optimierung, bestellt der Hook
   * nach einem Sync keinen Befund. Der Lauf bestellt am Ende selbst den
   * Nachher-Befund (finalizeJob); ein Nachher-Job ohne Quelle, der dann noch
   * laeuft, unterdrueckte ihn (enqueue-score-batches, 'live-job').
   */
  optimizationRunning = false,
) {
  const { user } = useAuth();
  const { toast } = useNotifications();
  const { triggerReauth } = useReauth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // Progress bar is now time-based (useSmoothProgress in the dialog).
  // No need for page/estimate calculations here.

  const [localJobInfo, setLocalJobInfo] = useState<{ jobId: string } | null>(null);
  const lastJobIdRef = useRef<string | null>(null);
  const [fetching, setFetchingState] = useState(false);
  // Spiegel des `fetching`-States fuer den Guard in handleFetchProducts. Der
  // Reauth-Retry (handleReauthRequiredJob -> triggerReauth-onSuccess) haelt eine
  // eingefrorene Closure aus dem Render, in dem der Sync LIEF (fetching=true) —
  // ein Guard auf dem State-Wert der Closure verschluckt den versprochenen
  // Retry nach dem Google-Reconnect dauerhaft. Der Ref wird im Setter (nicht
  // per Effect) mitgefuehrt: ein true->false im selben Batch koaleszierte sonst
  // zu "kein Change" und der Effect liefe nie — der Ref bliebe haengen.
  const fetchingRef = useRef(false);
  const setFetching = (value: boolean) => {
    fetchingRef.current = value;
    setFetchingState(value);
  };
  const [fetchProgress, setFetchProgress] = useState<{
    count: number;
    status: 'connecting' | 'fetching' | 'processing' | 'complete';
    page: number;
    syncStats?: SyncStats;
    recentTitles?: string[];
  }>({ count: 0, status: 'connecting', page: 0 });
  const [showFetchDialog, setShowFetchDialog] = useState(false);

  const cancelledRef = useRef(false);
  const [isStale, setIsStale] = useState(false);
  const reauthPromptedRef = useRef(false);
  // Doppel-Submit-Schutz (Muster useFeedCheckOptimize/scoreStartRef): `fetching`
  // allein faengt Same-Tick-Doppelklicks nicht — setState wirkt nicht synchron,
  // beide Klicks laesen dasselbe stale `false`. Ein Ref mutiert sofort.
  // Serverseitig ist der zweite Request bei Bezahl-Plaenen destruktiv:
  // enqueue-product-sync cancelt den laufenden Job, bevor es einen neuen anlegt.
  const fetchInFlightRef = useRef(false);
  // NA1 Task 1b: der Abschluss-Effekt liest den Laufzustand zum Zeitpunkt des
  // Sync-Endes, nicht beim Rendern.
  const optimizationRunningRef = useRef(optimizationRunning);
  useEffect(() => {
    optimizationRunningRef.current = optimizationRunning;
  }, [optimizationRunning]);
  const STALE_THRESHOLD_MS = 90_000; // 90s without heartbeat = function is stuck
  const [feedProductCount, setFeedProductCount] = useState<number | null>(null);

  // After sync completes, check if this specific feed has any products in the DB.
  // The sync downloads ALL MC products (benefiting all feeds), but if THIS feed's
  // data_source has 0 products, we warn the user.
  const refreshFeedProductCount = async () => {
    if (!feedId) return;
    const { count } = await supabase.from('products')
      .select('*', { count: 'exact', head: true })
      .eq('feed_id', feedId);
    setFeedProductCount(count ?? null);
  };

  // A sync that died because the Google connection was revoked writes the
  // machine-readable marker GOOGLE_REAUTH_REQUIRED into product_sync_jobs.error_message
  // instead of Google's raw 401 JSON. Both failure branches below switch on it so the
  // user gets one readable sentence plus a way back, not a backend payload.
  const isReauthJobError = (errorMessage: unknown): boolean =>
    errorMessage === 'GOOGLE_REAUTH_REQUIRED';

  // Die uebrigen Codes (GOOGLE_TOKEN_UNAVAILABLE, GOOGLE_GRANT_UNAVAILABLE) duerfen den
  // Reauth-Dialog NICHT oeffnen, aber auch nicht roh angezeigt werden. Ihre Saetze
  // stehen in `describeJobError` auf Modulebene (oben, mit eigenem Test).

  const handleReauthRequiredJob = async () => {
    setShowFetchDialog(false);
    // Everything below runs at most once per fetch attempt. Both failure branches can
    // fire for the same job, and the toast used to sit above this guard — so the user
    // got the same destructive message twice.
    if (reauthPromptedRef.current) return;
    reauthPromptedRef.current = true;
    toast({
      title: i18n.t('dashboard:toasts.reauthRequiredTitle'),
      description: i18n.t('dashboard:toasts.reauthRequiredDesc'),
      variant: 'destructive',
    });
    if (!feedId) return;
    // Resolve the affected account through the feed's ownership chain rather than
    // taking any flagged row: with two connected Google accounts that picked a
    // nondeterministic one and offered the wrong account for reconnection.
    // Deliberately not filtered on auth_status either — the account behind this feed
    // IS the affected one, and not every failure path manages to write the flag first.
    const account = await resolveGoogleAccountForFeed(feedId);
    if (account?.id) {
      triggerReauth(
        {
          googleAccountId: account.id,
          googleAccountEmail: account.email ?? undefined,
        },
        () => {
          // The banner reads its own query and otherwise only learns about the healed
          // auth_status through the realtime socket — invalidate here so a dropped
          // socket cannot leave it standing on top of a working connection.
          queryClient.invalidateQueries({ queryKey: [GOOGLE_AUTH_STATUS_QUERY_KEY] });
          // Retry the sync once the connection is back. Without this the user walks
          // through Google's consent screen and lands on the same empty dashboard.
          void handleFetchProducts();
        },
      );
    }
  };

  // Poll job table + realtime subscription via generic hook (useAsyncJob unchanged)
  const { activeJob, cancelJob, invalidate } = useAsyncJob<ProductSyncJob>({
    tableName: 'product_sync_jobs',
    channelName: 'product-sync-updates',
    queryKeyPrefix: 'product-sync-job',
    feedId,
    forcePolling: !!localJobInfo,
  });

  // Note: we intentionally do NOT clear localJobInfo when activeJob arrives.
  // The 1s fast-poll must keep running until the job is completed/failed,
  // because useAsyncJob only queries 'processing' rows every 3s.
  // Fast jobs complete between polls — without the fast-poll they'd leave
  // the dialog stuck at 0 products synced.

  // Auto-show dialog when an active job is detected (e.g. after page reload).
  // Suppressed during onboarding so the wizard's own download UI is the only one.
  useEffect(() => {
    if (suppressAutoDialog) return;
    if (activeJob?.status === 'processing' && !showFetchDialog) {
      setShowFetchDialog(true);
      setFetching(true);
    }
  }, [activeJob?.id, suppressAutoDialog]);

  // Mirror job progress into fetchProgress state for FetchProgressDialog
  useEffect(() => {
    if (!activeJob) return;
    if (activeJob.status === 'processing') {
      setFetchProgress({
        count: activeJob.total_fetched,
        status: 'fetching',
        page: activeJob.total_pages,
        recentTitles: activeJob.recent_titles ?? [],
      });
      const hb = activeJob.last_heartbeat_at ? new Date(activeJob.last_heartbeat_at).getTime() : 0;
      setIsStale(hb > 0 && Date.now() - hb > STALE_THRESHOLD_MS);
    } else if (activeJob.status === 'completed') {
      setIsStale(false);
      setFetchProgress({
        count: activeJob.total_fetched,
        status: 'complete',
        page: activeJob.total_pages,
        syncStats: {
          new: activeJob.stats_new ?? 0,
          updated: activeJob.stats_updated ?? 0,
          deleted: activeJob.stats_deleted ?? 0,
        },
      });
      refreshFeedProductCount();
    }
  }, [activeJob]);

  // Fast-completion guard: poll the specific job directly every 1s while localJobInfo
  // is set. useAsyncJob polls every 3s and only queries 'processing' status — fast jobs
  // (< 3s) complete before the first poll fires and would leave the dialog stuck.
  useEffect(() => {
    if (!localJobInfo?.jobId) return;

    const interval = setInterval(async () => {
      const { data: job } = await supabase
        .from('product_sync_jobs')
        .select('*')
        .eq('id', localJobInfo.jobId)
        .maybeSingle();

      if (!job) return;

      if (job.status === 'processing') {
        setFetchProgress({ count: job.total_fetched, status: 'fetching', page: job.total_pages, recentTitles: job.recent_titles ?? [] });
        const hb = job.last_heartbeat_at ? new Date(job.last_heartbeat_at).getTime() : 0;
        setIsStale(hb > 0 && Date.now() - hb > STALE_THRESHOLD_MS);
      } else if (job.status === 'completed') {
        setIsStale(false);
        const applyComplete = (j: typeof job) => setFetchProgress({
          count: j.total_fetched,
          status: 'complete',
          page: j.total_pages,
          syncStats: { new: j.stats_new ?? 0, updated: j.stats_updated ?? 0, deleted: j.stats_deleted ?? 0 },
        });
        // If total_fetched is 0, the DB may not have committed the final count yet.
        // Retry once after 800ms to pick up the actual count.
        if (job.total_fetched === 0) {
          await new Promise((r) => setTimeout(r, 800));
          const { data: retried } = await supabase
            .from('product_sync_jobs')
            .select('*')
            .eq('id', localJobInfo.jobId)
            .maybeSingle();
          if (retried) applyComplete(retried);
        } else {
          applyComplete(job);
        }
        refreshFeedProductCount();
        clearInterval(interval);
        setLocalJobInfo(null);
      } else if (job.status === 'failed' || job.status === 'cancelled') {
        setIsStale(false);
        // Show error toast for failed jobs so user knows sync didn't succeed
        if (job.status === 'failed') {
          if (isReauthJobError(job.error_message)) {
            void handleReauthRequiredJob();
          } else {
            setShowFetchDialog(false);
            toast({
              title: i18n.t('dashboard:toasts.fetchFailed'),
              description: describeJobError(job.error_message),
              variant: 'destructive',
            });
          }
        }
        clearInterval(interval);
        setLocalJobInfo(null);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [localJobInfo?.jobId]);

  const isFetching = !!activeJob || !!localJobInfo;
  const prevIsFetching = useRef(isFetching);
  const fetchProgressStatusRef = useRef<string>('connecting');

  // Keep fetchProgressStatusRef in sync so completion handler can read it without deps
  useEffect(() => {
    fetchProgressStatusRef.current = fetchProgress.status;
  }, [fetchProgress.status]);

  // Detect completion (isFetching transitions true → false)
  useEffect(() => {
    if (prevIsFetching.current && !isFetching) {
      if (!cancelledRef.current) {
        const jobId = lastJobIdRef.current;
        const needsStats = fetchProgressStatusRef.current !== 'complete';
        const startPreScore = async () => {
          // NA1 Task 1b: waehrend eines Laufs bestellt der Sync keinen Befund,
          // der Lauf liefert ihn am Ende selbst (siehe Parameter oben).
          if (optimizationRunningRef.current) return;
          // A2-NEU-3 (Entscheidung Dennis 06.10.2026): hat der Feed optimierte
          // Produkte, wird der Nachher-Befund bestellt. Ein Vorher-Befund waere
          // sonst der juengste und ersetzte in der Anzeige den Nachher-Befund.
          // Zaehlung unbekannt oder gescheitert heisst pre_optimization
          // (bisheriges Verhalten).
          let completedCount: number | null = null;
          if (feedId) {
            try {
              const { count, error: countError } = await supabase
                .from('products')
                .select('*', { count: 'exact', head: true })
                .eq('feed_id', feedId)
                .eq('optimization_status', 'completed');
              if (countError) {
                Sentry.captureException(countError, { tags: { feature: 'fetch', action: 'score-type-count' } });
              } else {
                completedCount = count ?? null;
              }
            } catch (err) {
              Sentry.captureException(err, { tags: { feature: 'fetch', action: 'score-type-count' } });
            }
          }
          supabase.functions.invoke('enqueue-score-batches', {
            body: { feed_id: feedId, score_type: scoreTypeForFeed(completedCount), user_id: user?.id, notify_feed_check: getFlowIntent() === 'feedcheck' }
          }).then((res) => {
            if (res.data?.job_id) {
              setScoreJobInfo({ jobId: res.data.job_id, totalProducts: res.data.total_products });
            }
            queryClient.invalidateQueries({ queryKey: ['score-calculation-job', feedId] });
          }).catch(err => {
            console.error('Pre-score calculation failed:', err);
            Sentry.captureException(err, { tags: { feature: 'fetch', action: 'pre-score' } });
          });
        };

        if (jobId) {
          // WP5 A1-V01: den Job IMMER lesen, wenn wir ihn kennen. Der Status
          // entscheidet, ob ein Pre-Score startet; die Zaehler-Anzeige laeuft
          // wie bisher nur, wenn der Dialog noch nicht auf 'complete' stand.
          Promise.resolve(
            supabase
              .from('product_sync_jobs')
              .select('*')
              .eq('id', jobId)
              .maybeSingle(),
          )
            .then(({ data: job, error: jobReadError }) => {
              if (jobReadError) {
                // WP5 A1-V01 Fix-Runde 1: ein gescheiterter Read entscheidet
                // jetzt ueber den Pre-Score (shouldStartPreScore(null, ...)
                // liefert false), das darf nicht stumm bleiben, sonst faellt
                // der Pre-Score nach einem ERFOLGREICHEN Sync unbemerkt aus.
                console.error('Job-Read fuer Pre-Score-Gate fehlgeschlagen:', jobReadError);
                Sentry.captureException(jobReadError, { tags: { feature: 'fetch', action: 'pre-score-gate' } });
              }
              if (job && needsStats) {
                if (job.status === 'failed') {
                  if (isReauthJobError(job.error_message)) {
                    void handleReauthRequiredJob();
                  } else {
                    setShowFetchDialog(false);
                    toast({
                      title: i18n.t('dashboard:toasts.fetchFailed'),
                      description: describeJobError(job.error_message),
                      variant: 'destructive',
                    });
                  }
                } else {
                  setFetchProgress({
                    count: job.total_fetched,
                    status: 'complete',
                    page: job.total_pages,
                    syncStats: {
                      new: job.stats_new ?? 0,
                      updated: job.stats_updated ?? 0,
                      deleted: job.stats_deleted ?? 0,
                    },
                  });
                }
              }
              if (shouldStartPreScore(job ?? null, feedId, true)) void startPreScore();
            })
            .catch((err) => {
              // Eine echte Rejection (Netzwerk, Client-Fehler) statt eines
              // PostgREST-error-Felds, dasselbe stumme Ausfallmuster.
              console.error('Job-Read fuer Pre-Score-Gate fehlgeschlagen:', err);
              Sentry.captureException(err, { tags: { feature: 'fetch', action: 'pre-score-gate' } });
            });
        } else if (shouldStartPreScore(null, feedId, false)) {
          void startPreScore();
        }

        // Cache invalidation — use prefix matching so new MC-based query keys are covered
        refetchStats();
        queryClient.invalidateQueries({ queryKey: ['feeds'] });
        queryClient.invalidateQueries({ queryKey: ['merchantCenters'] });
        queryClient.invalidateQueries({ queryKey: ['simple-products'] });
        queryClient.invalidateQueries({ queryKey: ['advanced-products'] });
        queryClient.invalidateQueries({ queryKey: ['simple-product-stats'] });
        queryClient.invalidateQueries({ queryKey: ['total-products'] });
        // K8-Befund: der Sync ist ein externer Schreiber auf ALLE Werkbank-
        // Zahl-Quellen (loescht/erzeugt auch pending_review/approved-Zeilen),
        // invalidierte aber nur simple-product-stats — das Seitenleisten-
        // Abzeichen und die Reiterzahlen zeigten fuer den Rest der Sitzung
        // den Vor-Sync-Stand (dieselbe Fehlerklasse wie Nr. 63, dort als
        // 'sechster Schluessel' im Optimierungs-Sweep behoben).
        queryClient.invalidateQueries({ queryKey: ['workbench-products'] });
        queryClient.invalidateQueries({ queryKey: ['workbench-server-total'] });
        queryClient.invalidateQueries({ queryKey: ['workbench-pending-count'] });
        queryClient.invalidateQueries({ queryKey: ['workbench-status-counts'] });
        queryClient.invalidateQueries({ queryKey: ['workbench-bulk-summary'] });
      }
      cancelledRef.current = false;
      setFetching(false);
    }
    prevIsFetching.current = isFetching;
  }, [isFetching]);

  // Keep public fetching state in sync with job activity
  useEffect(() => {
    if (isFetching && !fetching) setFetching(true);
  }, [isFetching]);

  const handleFetchProducts = async () => {
    if (!feedId) return;
    // Ref = Same-Tick-Doppelklick waehrend des Enqueue-Roundtrips (1-3 s ohne
    // sichtbares Feedback); `fetchingRef` = spaeterer Klick, waehrend der Sync
    // noch laeuft. Beides wuerde serverseitig den laufenden Job abbrechen.
    // BEWUSST der Ref statt `fetching`: der Reauth-Retry ruft diese Funktion
    // aus einer eingefrorenen Closure (fetching=true) auf, lange nachdem der
    // Live-State auf false gekippt ist — der State-Wert wuerde ihn verschlucken.
    if (fetchInFlightRef.current || fetchingRef.current) return;
    fetchInFlightRef.current = true;
    cancelledRef.current = false;
    reauthPromptedRef.current = false;
    setFetching(true);
    setFeedProductCount(null);
    setFetchProgress({ count: 0, status: 'fetching', page: 0 });

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Not authenticated');

      // Use fetch() directly to preserve the GOOGLE_REAUTH_REQUIRED error handling
      // (identical pattern to original useFetchProducts)
      const response = await fetch(`${FUNCTIONS_BASE}/enqueue-product-sync`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
          'apikey': session.access_token,
        },
        // notify_feed_check landet am Sync-Job: bei Completion enqueued der
        // DB-Trigger trg_feed_check_score den Score-Job server-seitig — die
        // Report-Mail kommt damit auch, wenn der Tab vorher geschlossen wird.
        body: JSON.stringify({ feedId, notify_feed_check: getFlowIntent() === 'feedcheck' }),
      });

      if (!response.ok) {
        let errorData: SyncEnqueueErrorBody = {};
        try { errorData = await response.json(); } catch { /* ignore parse errors */ }

        if (errorData.code === 'GOOGLE_REAUTH_REQUIRED') {
          setFetching(false);
          // `?? ''` ist laufzeitneutral und nicht Kosmetik: der ReauthProvider
          // rendert den Dialog nur bei `state.open && state.googleAccountId`,
          // ein leerer String verhaelt sich dort genau wie das fehlende Feld
          // (beides falsy, kein Dialog). Dasselbe Muster wie in
          // `GoogleAccounts.tsx` / `MerchantCenters.tsx`.
          triggerReauth(
            { ...errorData, googleAccountId: errorData.googleAccountId ?? '' },
            () => handleFetchProducts(),
          );
          return;
        }

        // Konvergenz 4, Befund 4: die 429 ist eine DESIGNTE Produkt-Antwort
        // (Free-Plan: 1 manueller Sync pro Feed und 24 h; der naechtliche
        // Auto-Sync zaehlt ins Fenster), kein Defekt. Sie fiel bisher in den
        // generischen Fehlerpfad: destruktiver Toast "Abruf fehlgeschlagen"
        // mit dem rohen englischen Server-Satz. Jetzt: nicht-destruktiver,
        // lokalisierter Zustand mit genau einer Handlung (Upgrade). Ein
        // konkretes "wieder moeglich ab" nennt die Antwort nicht — der Text
        // bleibt deshalb beim ehrlichen 24-h-Fenster.
        if (errorData.error === 'SYNC_RATE_LIMITED') {
          setFetching(false);
          toast({
            tone: 'warning',
            title: i18n.t('dashboard:toasts.syncRateLimitedTitle'),
            description: i18n.t('dashboard:toasts.syncRateLimitedDesc'),
            action: {
              label: i18n.t('dashboard:toasts.syncRateLimitedAction'),
              onClick: () => navigate('/pricing'),
            },
            durationMs: 10_000,
          });
          return;
        }

        throw new Error((errorData.message as string) || (errorData.error as string) || `Enqueue failed (${response.status})`);
      }

      const data = await response.json();
      lastJobIdRef.current = data.job_id;
      setLocalJobInfo({ jobId: data.job_id });
      setShowFetchDialog(true);
      invalidate();

    } catch (error) {
      Sentry.captureException(error, { tags: { feature: 'fetch', action: 'enqueue' } });
      setShowFetchDialog(false);
      setFetching(false);
      toast({
        title: i18n.t('dashboard:toasts.fetchFailed'),
        description: error instanceof Error ? error.message : i18n.t('dashboard:toasts.unknownError'),
        variant: 'destructive',
      });
    } finally {
      // Nach dem Roundtrip uebernimmt `fetching` den Schutz (bleibt bis
      // Job-Abschluss true); nach Fehlern gibt das Release den Retry frei.
      fetchInFlightRef.current = false;
    }
  };

  const cancelFetch = async () => {
    if (!feedId) return;
    cancelledRef.current = true;
    try {
      const jobId = activeJob?.id || localJobInfo?.jobId;
      if (jobId) await cancelJob(jobId);
      setLocalJobInfo(null);
      setFetching(false);
      setShowFetchDialog(false);
    } catch (err) {
      // WP0/K01: cancelJob wirft seit cancel_async_job bei jedem Serverfehler
      // (fremder Job 42501, 57014 beim Staging-Raeumen eines grossen Syncs). Der
      // Sync laeuft dann weiter: Dialog offen lassen, den Abschluss-Toast wieder
      // zulassen und den Fehler zeigen, statt ihn nur zu loggen.
      cancelledRef.current = false;
      Sentry.captureException(err, { tags: { feature: 'fetch', action: 'cancel' } });
      toast({
        title: i18n.t('dashboard:toasts.cancelFailed'),
        description: err instanceof Error ? err.message : i18n.t('dashboard:toasts.unknownError'),
        variant: 'destructive',
      });
    }
  };

  return {
    fetching,
    fetchProgress,
    showFetchDialog,
    setShowFetchDialog,
    handleFetchProducts,
    cancelFetch,
    isStale,
    feedProductCount,
  };
}
